import { getOldestDeviation } from 'helper/getOldestDeviation';
import { setSeverity } from 'helper/setSeverity';
import type { DeviationWithVIN } from 'helper/types';
import { flattenV2Deviations } from 'helper/vehicleV2';
import { mapValues, pipe, prop, sortBy } from 'remeda';
import type { GenericRecord } from 'services/utils';

import type { PrioritezedItem } from './types';

import { MRMStateValue, Severity, type VehicleV2 } from 'API';

export const prioritizeDeviations = (vehicles?: GenericRecord<VehicleV2>) => {
    if (!vehicles) {
        return;
    }
    return pipe(
        Object.values(vehicles),
        _ =>
            _.reduce(
                (pre: Record<string, DeviationWithVIN[]>, cur) => ({
                    ...pre,
                    [cur.vin]: flattenV2Deviations(cur.deviations).map(
                        deviation => ({ ...deviation, vin: cur.vin }) as DeviationWithVIN
                    ),
                }),
                {}
            ),
        mapValues(sortBy([prop('sentAt'), 'asc'])),
        Object.values,
        sortedDeviations =>
            sortedDeviations.map(sortedDeviation => {
                const fatalDeviation = sortedDeviation.find(
                    (deviation: DeviationWithVIN) => deviation.severity === Severity.FATAL
                );
                const errorDeviation = sortedDeviation.find(
                    (deviation: DeviationWithVIN) => deviation.severity === Severity.ERROR
                );
                const warningDeviation = sortedDeviation.find(
                    (deviation: DeviationWithVIN) => deviation.severity === Severity.WARNING
                );

                switch (true) {
                    case !!fatalDeviation:
                        return fatalDeviation;
                    case !!errorDeviation:
                        return errorDeviation;
                    case !!warningDeviation:
                        return warningDeviation;
                    default:
                        return;
                }
            }),
        (_: DeviationWithVIN[]) => ({
            fatals: sortBy(
                _.filter(deviation => deviation?.severity === Severity.FATAL),
                [prop('sentAt'), 'asc']
            ),
            errors: sortBy(
                _.filter(deviation => deviation?.severity === Severity.ERROR),
                [prop('sentAt'), 'asc']
            ),
            warnings: sortBy(
                _.filter(deviation => deviation?.severity === Severity.WARNING),
                [prop('sentAt'), 'asc']
            ),
            infos: sortBy(
                _.filter(deviation => deviation?.severity === Severity.INFO),
                [prop('sentAt'), 'asc']
            ),
        })
    );
};

export const getLastCharacters = (value: string, numberOfCharacters: number) => value.slice(-numberOfCharacters);

const mrmOrdering = [
    MRMStateValue.EXECUTING_MRM,
    MRMStateValue.MRC,
    MRMStateValue.RELEASING_MRC,
    MRMStateValue.MRM_FAULTED,
    MRMStateValue.ERROR,
    MRMStateValue.NONE,
];

export const sortByMRMState = (vehicles?: PrioritezedItem[], severity?: Severity) => {
    if (!vehicles) {
        return [];
    }

    if (!severity) {
        return vehicles.sort(
            (crr, nxt) => mrmOrdering.indexOf(crr.mrmState.value) - mrmOrdering.indexOf(nxt.mrmState.value)
        );
    }

    return vehicles
        .filter(vehicle => vehicle.severity === severity)
        .sort((crr, nxt) => mrmOrdering.indexOf(crr.mrmState.value) - mrmOrdering.indexOf(nxt.mrmState.value));
};

const severityOrdering = [Severity.FATAL, Severity.ERROR, Severity.WARNING, Severity.INFO, Severity.UNKNOWN];

export const sortBySeverity = (items: PrioritezedItem[]) =>
    items?.sort((crr, nxt) => severityOrdering.indexOf(crr.severity) - severityOrdering.indexOf(nxt.severity));

export const parseIntoPVLItems = (
    vehicles: VehicleV2[]
): { inMRM: PrioritezedItem[]; others: PrioritezedItem[] } | undefined => {
    if (!vehicles) {
        return {
            inMRM: [],
            others: [],
        };
    }

    const isInMRMProcess = (vehicle: PrioritezedItem) =>
        vehicle.mrmState.value === MRMStateValue.EXECUTING_MRM ||
        vehicle.mrmState.value === MRMStateValue.MRC ||
        vehicle.mrmState.value === MRMStateValue.RELEASING_MRC;

    const isNotInMRMProcess = (vehicle: PrioritezedItem) =>
        vehicle.mrmState.value !== MRMStateValue.EXECUTING_MRM &&
        vehicle.mrmState.value !== MRMStateValue.MRC &&
        vehicle.mrmState.value !== MRMStateValue.RELEASING_MRC;

    const parsedItems = vehicles.map(vehicle => {
        const deviations = flattenV2Deviations(vehicle.deviations);
        return {
            vin: vehicle.vin,
            name: vehicle.name,
            licensePlate: vehicle.licensePlate,
            severity: setSeverity(deviations),
            mrmState: {
                value: vehicle.mrmState?.value?.data?.value ?? MRMStateValue.NONE,
                updatedAt: vehicle.mrmState?.value?.updatedAt ?? '',
            },
            relativeTime: getOldestDeviation(deviations),
            missionId: vehicle.activeMissionId?.value?.data,
            connectivityState: vehicle.connectivityStatus?.value?.state,
        };
    });

    return pipe(Object.values(parsedItems), _ => ({
        inMRM: sortBySeverity(_.filter(vehicle => isInMRMProcess(vehicle))),
        others: sortBySeverity(
            _.filter(
                vehicle => (!vehicle.mrmState || isNotInMRMProcess(vehicle)) && vehicle.severity !== Severity.UNKNOWN
            )
        ),
    }));
};
